import { object } from 'yup';
import { BaseModel } from '../base.model';
import { JsonObject } from '../../helpers';
import { capitalize, flatten } from 'lodash';
import {
  AssistantData,
  ClientAddr,
  ClientIdentifiers,
  UserEventConnectors,
  FileUpload,
  FirewallDecision,
  LegacyIntentScore,
  PolicyDecision,
  RequestMetadata,
  RiskAssessment,
  RiskLevel,
  SensitiveData,
  McpTool,
  UserClaim,
  UserEventIntent,
  UserEventIntents,
  UserEventMessage,
  Violation,
  IntentScore,
  RegexSdDetection,
  InferenceConnector,
} from '.';
import { getViolations, SurePathServiceName } from './helpers';
import { ConnectorModel } from '../connector/connector.model';
import { getTypedConnector } from '../connector';

export class UserEventModel extends BaseModel {
  asyncId: string;
  clientIdentifiers: ClientIdentifiers;
  clientIp: ClientAddr;
  code: number;
  conversationId: string;
  endTime: number;
  error: string;
  firewallDecision: FirewallDecision;
  intent: UserEventIntent;
  intents: UserEventIntents;
  messages: {
    requestId: string;
    conversation: UserEventMessage[];
    request: UserEventMessage[];
    response: UserEventMessage[];
    requestCount: number;
    responseCount: number;
    conversationCount: number;
  };
  orgId: string;
  parentTraceId: string;
  policyDecision: PolicyDecision;
  requestType: string;
  requestUrl: string;
  referrer: string;
  responseType: string;

  sensitiveData: {
    request: SensitiveData;
    response: SensitiveData;
  };
  serviceName: string;
  serviceId: string;
  spId: string;
  startTime: number;
  timestamp: number;
  traceId: string;
  requestId: string;
  userClaim: UserClaim;
  userId: string;
  riskAssessment?: RiskAssessment;
  metadata?: RequestMetadata;
  assistant?: AssistantData;
  tools?: McpTool[];
  fileUpload?: FileUpload;
  license?: {
    detectedLicenseName: string;
    acceptableLicenses: string[];
  };
  inference?: InferenceConnector;

  static schema = object({});

  constructor(props: JsonObject = {}) {
    super(props);

    const data = props as Record<keyof UserEventModel, unknown>;

    this.asyncId = (data.asyncId as string) || '';
    this.clientIdentifiers = (data.clientIdentifiers as ClientIdentifiers) || {
      userAgent: '',
    };
    this.clientIp = (data.clientIp as ClientAddr) || {
      remoteIp: '',
      xForwardedFor: '',
      xRealIp: '',
      internalIp: '',
    };
    this.code = (data.code as number) || 0;
    this.conversationId = (data.conversationId as string) || '';
    this.endTime = (data.endTime as number) || 0;
    this.error = (data.error as string) || '';
    this.firewallDecision = (data.firewallDecision as FirewallDecision) || {
      action: '',
      target: { name: '' },
    };

    this.intent = (data.intent as UserEventIntent) || {
      requestViolation: false,
      responseViolation: false,
      request: [],
      response: [],
    };

    this.intents = (data.intents as UserEventIntents) || {
      input: {
        domains: [],
        actions: [],
        topics: [],
      },
      output: {
        domains: [],
        actions: [],
        topics: [],
      },
    };

    this.messages = (data.messages as {
      requestId: string;
      conversation: UserEventMessage[];
      request: UserEventMessage[];
      response: UserEventMessage[];
      requestCount: number;
      responseCount: number;
      conversationCount: number;
    }) || {
      requestId: '',
      conversation: [],
      request: [],
      response: [],
      requestCount: 0,
      responseCount: 0,
      conversationCount: 0,
    };

    this.orgId = (data.orgId as string) || '';
    this.parentTraceId = (data.parentTraceId as string) || '';
    this.policyDecision = (data.policyDecision as PolicyDecision) || {
      decision: 0,
      label: '',
      code: 0,
      codeLabel: '',
      violations: {
        sensitiveData: false,
        intent: false,
        access: false,
        output: false,
        input: false,
        pii: false,
        confidentialData: false,
        promptInjection: false,
        toxicity: false,
        code: false,
        bias: false,
        risk: false,
        tool: false,
        fileUpload: false,
        regex: false,
      },
      isGroupPolicy: false,
      groupId: '',
    };

    this.requestType = (data.requestType as string) || '';
    this.requestUrl = (data.requestUrl as string) || '';
    this.referrer = (data.referrer as string) || '';
    this.responseType = (data.responseType as string) || '';

    this.sensitiveData = (data.sensitiveData as {
      request: SensitiveData;
      response: SensitiveData;
    }) || {
      request: { detected: false, detections: [] },
      response: { detected: false, detections: [] },
    };

    this.traceId = (data.traceId as string) || '';
    this.requestId = (data.requestId as string) || '';
    this.startTime = (data.startTime as number) || 0;
    this.timestamp = (data.timestamp as number) || 0;
    this.serviceName = (data.serviceName as string) || '';
    this.serviceId = (data.serviceId as string) || '';
    this.spId = (data.spId as string) || '';

    this.userClaim = {
      userId: '',
      name: '',
      email: '',
      orgId: '',
      role: '',
      groups: [],
      spUserId: '',
    };

    if (data.userClaim) {
      this.userClaim = UserEventModel.makeUserClaim(data.userClaim as Record<string, unknown>);
    }

    this.userId = (data.userId as string) || '';

    if (data.riskAssessment) {
      this.riskAssessment = data.riskAssessment as RiskAssessment;
    }

    if (data.metadata) {
      this.metadata = Object.fromEntries(
        Object.entries(data.metadata as RequestMetadata).filter(
          ([, { adminVisible }]) => adminVisible
        )
      );
    }

    if (data.assistant) {
      this.assistant = data.assistant as AssistantData;
    }

    if (Array.isArray((data.tools as { mcpTools?: McpTool[] })?.mcpTools)) {
      this.tools = (data.tools as { mcpTools: McpTool[] }).mcpTools;
    }

    if (data.fileUpload) {
      this.fileUpload = data.fileUpload as FileUpload;
    }

    if (data.license) {
      this.license = data.license as {
        detectedLicenseName: string;
        acceptableLicenses: string[];
      };
    }

    if (data.inference) {
      this.inference = data.inference as InferenceConnector;
    }
  }

  get connectors(): UserEventConnectors {
    const connectors: UserEventConnectors = {};
    const { ingressOrgConnector } = this.clientIdentifiers;

    if (ingressOrgConnector?.id && ingressOrgConnector?.type) {
      connectors.ingress = getTypedConnector(new ConnectorModel(ingressOrgConnector));
    }

    if (this.inference) {
      const { connectorId, connectorType, connectorName } = this.inference;
      connectors.inference = getTypedConnector(
        new ConnectorModel({
          id: connectorId,
          type: connectorType,
          name: connectorName,
        })
      );
    }

    return connectors;
  }

  static makeUserClaim(data: Record<string, unknown>): UserClaim {
    const { name, email, org_id, role, user_id, sp_user_id, groups } = data;
    return {
      name: name as string,
      email: email as string,
      role: role as string,
      orgId: org_id as string,
      userId: user_id as string,
      groups: groups as string[],
      spUserId: sp_user_id as string,
    };
  }

  get requestHost(): string | null {
    try {
      const requestURL = new URL(this.requestUrl);
      return requestURL.host;
    } catch {
      return '';
    }
  }

  get legacyHighIntent(): LegacyIntentScore | null {
    let highIntent: LegacyIntentScore | null = null;

    if (this.intent.request?.length) {
      highIntent = this.intent.request?.reduce((high, current) =>
        current.confidence > high.confidence ? current : high
      );
    }

    if (!highIntent) {
      return null;
    }

    // If action or department are not defined, try to parse them out of the name field
    if ((!highIntent.actionName || !highIntent.departmentName) && highIntent.name) {
      if (highIntent.name === '///') {
        return null;
      }
      const [, departmentName, actionName] = String(highIntent.name).split('/');
      if (departmentName && !highIntent.departmentName) {
        highIntent.departmentName = departmentName;
      }

      if (actionName && !highIntent.actionName) {
        highIntent.actionName = actionName;
      }
    }

    return {
      ...highIntent,
      departmentName: capitalize(highIntent.departmentName),
      actionName: capitalize(highIntent.actionName),
    };
  }

  get legacyIntentLabel(): string {
    if (!this.legacyHighIntent) {
      return '';
    }

    const { actionName, departmentName } = this.legacyHighIntent;

    return `${[departmentName, actionName]
      .filter((val) => val)
      .map(capitalize)
      .join(' / ')}`;
  }

  get intentLabels(): { domains: string; actions: string; topics: string } {
    const { domains, actions, topics } = this.intents.input;

    const domainLabels = domains ? domains.map(({ name }) => name).join(', ') : '';
    const actionLabels = actions ? actions.map(({ name }) => name).join(', ') : '';
    const topicLabels = topics ? topics.map(({ name }) => name).join(', ') : '';

    return {
      domains: domainLabels || this.legacyHighIntent?.departmentName || '',
      actions: actionLabels || this.legacyHighIntent?.actionName || '',
      topics: topicLabels || this.legacyHighIntent?.name || '',
    };
  }

  get customIntents(): IntentScore[] {
    return this.intents.input.topics.filter(({ id }) => !!id);
  }

  get violations(): Violation[] {
    return getViolations(this.policyDecision?.violations || {});
  }

  get lastUserMessage(): string | null {
    const userMessages = this.messages.request.filter(({ role }) => role === 'user');
    return userMessages.pop()?.content || null;
  }

  get sensitiveDataTypes(): string[] {
    const detections = [
      ...(this.sensitiveData.request.detections || []),
      ...(this.sensitiveData.response.detections || []),
    ];
    return flatten(detections.map((detection) => detection.map(({ type }) => type)));
  }

  get sensitiveDataRegexDetections(): RegexSdDetection[] {
    const detections = [
      ...(this.sensitiveData.request.regexDetections || []),
      ...(this.sensitiveData.response.regexDetections || []),
    ];
    return flatten(detections);
  }

  // for historic reasons, some requests do not have a traceId
  get compTraceId(): string {
    return this.traceId || this.requestId;
  }

  get riskLevel(): RiskLevel | null {
    return this.riskAssessment?.overallRiskLevel || null;
  }

  // @todo there's gotta be a better way to do this
  // Matches on SurePath AI and the SDK
  get isF5WAIS(): boolean {
    return (
      this.requestUrl.includes('surepath.ai') || this.serviceName.toLowerCase().includes('surepath')
    );
  }

  get productServiceName(): string {
    if (!this.isF5WAIS) {
      return '';
    }

    // Hardcoded for now, will be replaced when more routes are added
    if (this.requestUrl.includes('model-router') || this.requestUrl.includes('gateway')) {
      return SurePathServiceName.GATEWAY;
    }

    if (this.requestUrl.includes('edge')) {
      return SurePathServiceName.PORTAL;
    }

    if (this.requestUrl.includes('sdk')) {
      return SurePathServiceName.SDK;
    }

    return String(this.serviceName).toLowerCase().includes('surepath')
      ? SurePathServiceName.SUREPATH_AI
      : this.serviceName;
  }
}
